import { pxToRem } from '@fluentsms/agentnet-web-components';
import { Grid, IconButton, InputAdornment, Tooltip, Typography } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { Clear, HelpOutline, ReportProblemOutlined } from '@material-ui/icons';
import clsx from 'clsx';
import type { ChangeEvent } from 'react';
import { useEffect, useState } from 'react';
import { RateFeeReportedField } from 'core/layout-core/core-content/ReportsAndPayDrawer.utils';
import OverflowTip from 'ui-kit/components/utility/OverflowTip';
import { CurrencyField } from 'ui-kit/inputs';
import { calculateTotals, dollarize, hideFieldsWithStatus } from 'utilities/utilities';

const REMITTANCE_BASIS_TOOLTIP =
  'This amount is used to calculate the Underwriter Remitted value based on the Underwriter and Agent split agreement.';

// Base RateFee interface with recursion for two levels
interface RateFee {
  rateFeeTypeCdId: number;
  rateFeeType: string;
  rateFeeLineItemId: number;
  rateFeeDescription: string;
  policyNumber: string;
  isDisabled: boolean;
  grossCalculated: number;
  grossReported: number;
  isGrossOverridden: boolean;
  netCalculated: number;
  netReported: number;
  isNetOverridden: boolean;
  remittanceBasisCalculated?: number;
  remittanceBasisReported?: number;
  isRemittanceBasisOverridden?: boolean;
  isRemittanceBasisAmountEnabled?: boolean;
  isError?: boolean;
  isRemittanceBasisTooHigh?: boolean;
  isNetTooHigh?: boolean;
  status: string;
  additionalFees?: RateFee[]; // Recursively allows nested RateFees, up to 2 levels
}

// NestedRateFee restricts third-level nesting by disallowing additionalFees
type NestedRateFee = RateFee & { additionalFees?: never };

// This is a union type that combines the two possibilities
type RateFeeWithLimit = RateFee | NestedRateFee;

interface ReportedFeesTableProps {
  products?: RateFeeWithLimit[];
  onChangeProduct?: (
    value: number,
    rateFeeLineItemId: number,
    field: RateFeeReportedField,
    childId?: number,
    nestedChildId?: number,
  ) => void;
  onClear?: (rateFeeLineItemId: number, field: RateFeeReportedField, childId?: number, nestedChildId?: number) => void;
  qaPrefix: string;
  showValidation?: boolean;
  displayNet?: boolean;
  displayRemittanceBasis?: boolean;
  disableFields?: boolean;
  hasRemittedProduct?: boolean;
}

interface ProductDescriptionProps {
  data: RateFeeWithLimit;
  displayNet: boolean;
  displayRemittanceBasis: boolean;
  isChild?: boolean;
  isChildrenOfChild?: boolean;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    alignCenter: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    },
    warningOutlined: { '& path': { fill: theme.palette.warning.main } },
    reportedFeesTable: {
      width: '100%',
    },
    remittedFeesTable: {
      width: '100%',
      margin: '8px 10px 16px 8px',
    },
    reportedFeesTableHeader: {
      backgroundColor: '#F2F5F6',
      borderTop: `2px solid ${theme.palette.divider}`,
      borderBottom: `1px solid ${theme.palette.divider}`,
      fontWeight: 600,
      fontSize: pxToRem(12),
    },
    reportedFeesTableHeaderCell: {
      padding: theme.spacing(1.25),
    },
    remittanceBasisHeader: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: theme.spacing(0.5),
    },
    remittanceBasisHelpIcon: {
      cursor: 'pointer',
      flexShrink: 0,
    },
    tableRow: {
      borderBottom: `1px solid #E0E0E0`,
      minHeight: '56px',
    },
    tableCell: {
      padding: pxToRem(8),
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      textWrap: 'nowrap',
      textOverflow: 'ellipsis',
      overflow: 'hidden',
    },
    tableCellRight: {
      textAlign: 'right',
    },
    indented: {
      paddingLeft: theme.spacing(3),
    },
    indented2: {
      paddingLeft: theme.spacing(5),
    },
    totalsRow: {
      borderTop: `2px solid ${theme.palette.divider}`,
    },
    totalsCell: {
      padding: theme.spacing(1),
    },
    weight500: {
      fontWeight: 500,
    },
    my1: {
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(1),
    },
    mb1: {
      marginBottom: theme.spacing(1),
    },
    isTouched: {
      '& .MuiInputBase-root': {
        backgroundColor: theme.palette.agGrid.overrideBackground,
      },
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.agGrid.overrideBorder,
      },
      '& .MuiOutlinedInput-root': {
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderWidth: 2,
          borderColor: `${theme.palette.agGrid.overrideBorderHover} !important`,
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
          borderColor: `${theme.palette.agGrid.overrideBorderHover} !important`,
        },
      },
    },
    isError: {
      backgroundColor: '#FDECEA !important',
    },
    iconBtn: {
      '&:hover': {
        background: 'transparent',
      },
    },
    policyNumber: {
      color: theme.palette.text.secondary,
    },
  }),
);

const productDescriptionSm = (displayNet: boolean, displayRemittanceBasis: boolean) =>
  displayNet ? (displayRemittanceBasis ? 3 : 6) : displayRemittanceBasis ? 6 : 9;

const ProductDescription = ({
  data,
  displayNet,
  displayRemittanceBasis,
  isChild = false,
  isChildrenOfChild = false,
}: ProductDescriptionProps) => {
  const classes = useStyles();
  const isPending = data.status === 'Pending' && !isChild && !isChildrenOfChild;
  return (
    <Grid
      item
      sm={productDescriptionSm(displayNet, displayRemittanceBasis)}
      className={clsx(classes.tableCell, {
        [classes.indented]: isChild,
        [classes.indented2]: isChildrenOfChild,
      })}
    >
      <OverflowTip>
        <Typography
          variant="body2"
          className={clsx({
            [classes.alignCenter]: isPending,
          })}
        >
          {isPending && <ReportProblemOutlined className={classes.warningOutlined} fontSize="small" />}
          {data.rateFeeDescription}
          {data.policyNumber && !isChild && !isChildrenOfChild && (
            <>
              <br />
              <span className={classes.policyNumber}>{`Policy No: ${data.policyNumber}`}</span>
            </>
          )}
        </Typography>
      </OverflowTip>
    </Grid>
  );
};

interface InputFieldsProps {
  data: RateFeeWithLimit;
  qaPrefix: string;
  qaSuffix: string | number;
  displayNet: boolean;
  displayRemittanceBasis: boolean;
  disableFields: boolean;
  showValidation?: boolean;
  onGrossChangeProduct: (el: ChangeEvent<HTMLInputElement>) => void;
  onRemittanceBasisChangeProduct?: (el: ChangeEvent<HTMLInputElement>) => void;
  onNetChangeProduct: (el: ChangeEvent<HTMLInputElement>) => void;
  onGrossClearProduct: () => void;
  onRemittanceBasisClearProduct?: () => void;
  onNetClearProduct: () => void;
  hasRemittedProduct: boolean;
}

const InputFields = ({
  data,
  qaPrefix,
  qaSuffix,
  displayNet,
  displayRemittanceBasis,
  disableFields,
  showValidation,
  onGrossChangeProduct,
  onRemittanceBasisChangeProduct,
  onNetChangeProduct,
  onGrossClearProduct,
  onRemittanceBasisClearProduct,
  onNetClearProduct,
  hasRemittedProduct,
}: InputFieldsProps) => {
  const classes = useStyles();
  /** When the API locks remittance basis, never show override highlight/clear even if flags are stale. */
  const remittanceBasisLockedByProduct = data.isRemittanceBasisAmountEnabled === false;
  const grossDisabled = disableFields || data.isDisabled;
  const remittanceBasisDisabled = grossDisabled || remittanceBasisLockedByProduct;
  const netDisabled = grossDisabled;
  const hasRemittanceBasisFieldError = Boolean(data.isRemittanceBasisTooHigh) && showValidation;
  const hasNetFieldError = Boolean(data.isNetTooHigh) && showValidation;

  if (!hasRemittedProduct && hideFieldsWithStatus.includes(data.status)) {
    return null;
  }

  return (
    <>
      <Grid item sm={3} className={clsx(classes.tableCell)}>
        <CurrencyField
          variant="outlined"
          value={data.grossReported}
          fullWidth
          max={100000000000}
          allowNegative={false}
          data-testid={`${qaPrefix}Gross${qaSuffix}`}
          onChange={onGrossChangeProduct}
          className={clsx({ [classes.isTouched]: data.isGrossOverridden === true && !grossDisabled })}
          disabled={grossDisabled}
          alignRight
          //compact
          InputProps={{
            ...(data.isGrossOverridden && !grossDisabled
              ? {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        disableFocusRipple
                        disableRipple
                        disableTouchRipple
                        onClick={onGrossClearProduct}
                        edge="end"
                        className={classes.iconBtn}
                      >
                        <Clear htmlColor="#666666" data-testid={`${qaPrefix}DeleteGross${qaSuffix}`} />
                      </IconButton>
                    </InputAdornment>
                  ),
                }
              : {}),
          }}
        />
      </Grid>
      {displayRemittanceBasis && onRemittanceBasisChangeProduct && onRemittanceBasisClearProduct && (
        <Grid item sm={3} className={clsx(classes.tableCell)}>
          <CurrencyField
            variant="outlined"
            value={data.remittanceBasisReported}
            fullWidth
            max={100000000000}
            allowNegative={false}
            data-testid={`${qaPrefix}RemittanceBasis${qaSuffix}`}
            onChange={onRemittanceBasisChangeProduct}
            error={hasRemittanceBasisFieldError}
            className={clsx({
              [classes.isTouched]:
                data.isRemittanceBasisOverridden === true &&
                !remittanceBasisDisabled &&
                !remittanceBasisLockedByProduct,
              [classes.isError]: hasRemittanceBasisFieldError,
            })}
            disabled={remittanceBasisDisabled}
            alignRight
            InputProps={{
              ...(data.isRemittanceBasisOverridden && !remittanceBasisDisabled && !remittanceBasisLockedByProduct
                ? {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          disableFocusRipple
                          disableRipple
                          disableTouchRipple
                          onClick={onRemittanceBasisClearProduct}
                          edge="end"
                          className={classes.iconBtn}
                        >
                          <Clear htmlColor="#666666" data-testid={`${qaPrefix}DeleteRemittanceBasis${qaSuffix}`} />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }
                : {}),
            }}
          />
        </Grid>
      )}
      {displayNet && (
        <Grid item sm={3} className={clsx(classes.tableCell)}>
          <CurrencyField
            variant="outlined"
            value={data.netReported}
            error={hasNetFieldError}
            fullWidth
            max={100000000000}
            data-testid={`${qaPrefix}Net${qaSuffix}`}
            allowNegative={false}
            onChange={onNetChangeProduct}
            className={clsx({
              [classes.isTouched]: data.isNetOverridden === true && !netDisabled,
              [classes.isError]: hasNetFieldError,
            })}
            disabled={netDisabled}
            alignRight
            //compact
            InputProps={{
              ...(data.isNetOverridden && !netDisabled
                ? {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          disableFocusRipple
                          disableRipple
                          disableTouchRipple
                          onClick={onNetClearProduct}
                          edge="end"
                          className={classes.iconBtn}
                        >
                          <Clear htmlColor="#666666" data-testid={`${qaPrefix}DeleteNet${qaSuffix}`} />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }
                : {}),
            }}
          />
        </Grid>
      )}
    </>
  );
};

const accumulateRemittanceBasisTotal = (products: RateFeeWithLimit[], hasRemittedProduct: boolean) => {
  let remittanceBasisTotal = 0;
  const addLine = (line: RateFeeWithLimit) => {
    const skip = !hasRemittedProduct && hideFieldsWithStatus.includes(line.status);
    if (skip) return;
    const v = line.remittanceBasisReported ?? line.remittanceBasisCalculated ?? 0;
    remittanceBasisTotal += v;
  };
  products.forEach((prod) => {
    addLine(prod);
    prod.additionalFees?.forEach((ch) => {
      addLine(ch);
      ch.additionalFees?.forEach((z) => addLine(z));
    });
  });
  return remittanceBasisTotal;
};

const ReportedFeesTable = ({
  onChangeProduct,
  products = [],
  onClear,
  qaPrefix,
  showValidation,
  displayNet = true,
  displayRemittanceBasis = false,
  disableFields = true,
  hasRemittedProduct = false,
}: ReportedFeesTableProps) => {
  const classes = useStyles();
  const [grandTotal, setGrandTotal] = useState({
    gross: 0,
    net: 0,
    remittanceBasis: 0,
  });
  useEffect(() => {
    const base = calculateTotals(products, hasRemittedProduct);
    setGrandTotal({
      ...base,
      remittanceBasis: displayRemittanceBasis ? accumulateRemittanceBasisTotal(products, hasRemittedProduct) : 0,
    });
  }, [products, hasRemittedProduct, displayRemittanceBasis]);

  const productSm = productDescriptionSm(displayNet, displayRemittanceBasis);

  return (
    <>
      {products && products.length > 0 && (
        <div className={hasRemittedProduct ? classes.remittedFeesTable : classes.reportedFeesTable}>
          <Grid container className={classes.reportedFeesTableHeader}>
            <Grid item sm={productSm} className={classes.reportedFeesTableHeaderCell} style={{ paddingLeft: 16 }}>
              Product
            </Grid>
            <Grid item sm={3} className={classes.reportedFeesTableHeaderCell}>
              Gross Reported
            </Grid>
            {displayRemittanceBasis && (
              <Grid item sm={3} className={classes.reportedFeesTableHeaderCell}>
                <span className={classes.remittanceBasisHeader}>
                  Remittance Basis
                  <Tooltip title={REMITTANCE_BASIS_TOOLTIP} placement="top" arrow>
                    <HelpOutline
                      color="primary"
                      fontSize="small"
                      className={classes.remittanceBasisHelpIcon}
                      data-testid={`${qaPrefix}RemittanceBasisHelp`}
                    />
                  </Tooltip>
                </span>
              </Grid>
            )}
            {displayNet && (
              <Grid item sm={3} className={classes.reportedFeesTableHeaderCell}>
                Net Reported
              </Grid>
            )}
          </Grid>
          <Grid container>
            {products.map((e: RateFeeWithLimit, i: number) => (
              <>
                <Grid container key={e.rateFeeLineItemId} className={classes.tableRow}>
                  <ProductDescription
                    data={e}
                    displayNet={displayNet}
                    displayRemittanceBasis={displayRemittanceBasis}
                  />
                  <InputFields
                    data={e}
                    qaPrefix={qaPrefix}
                    qaSuffix={i + 1}
                    displayNet={displayNet}
                    displayRemittanceBasis={displayRemittanceBasis}
                    disableFields={disableFields}
                    showValidation={showValidation}
                    hasRemittedProduct={hasRemittedProduct}
                    onGrossChangeProduct={(el: ChangeEvent<HTMLInputElement>) => {
                      onChangeProduct &&
                        onChangeProduct(Number(el.target.value), e.rateFeeLineItemId, RateFeeReportedField.Gross);
                    }}
                    onRemittanceBasisChangeProduct={
                      displayRemittanceBasis
                        ? (el: ChangeEvent<HTMLInputElement>) => {
                            onChangeProduct &&
                              onChangeProduct(
                                Number(el.target.value),
                                e.rateFeeLineItemId,
                                RateFeeReportedField.RemittanceBasis,
                              );
                          }
                        : undefined
                    }
                    onNetChangeProduct={(el: ChangeEvent<HTMLInputElement>) => {
                      onChangeProduct &&
                        onChangeProduct(Number(el.target.value), e.rateFeeLineItemId, RateFeeReportedField.Net);
                    }}
                    onGrossClearProduct={() => onClear?.(e.rateFeeLineItemId, RateFeeReportedField.Gross)}
                    onRemittanceBasisClearProduct={
                      displayRemittanceBasis
                        ? () => onClear?.(e.rateFeeLineItemId, RateFeeReportedField.RemittanceBasis)
                        : undefined
                    }
                    onNetClearProduct={() => onClear?.(e.rateFeeLineItemId, RateFeeReportedField.Net)}
                  />
                </Grid>
                {e.additionalFees &&
                  e.additionalFees.map((c: RateFee, j: number) => (
                    <>
                      <Grid container key={c.rateFeeLineItemId} className={classes.tableRow}>
                        <ProductDescription
                          data={c}
                          isChild
                          displayNet={displayNet}
                          displayRemittanceBasis={displayRemittanceBasis}
                        />

                        <InputFields
                          data={c}
                          qaPrefix={qaPrefix}
                          qaSuffix={`${i + 1}${j + 1}`}
                          displayNet={displayNet}
                          displayRemittanceBasis={displayRemittanceBasis}
                          disableFields={disableFields}
                          showValidation={showValidation}
                          hasRemittedProduct={hasRemittedProduct}
                          onGrossChangeProduct={(el: ChangeEvent<HTMLInputElement>) => {
                            onChangeProduct?.(
                              Number(el.target.value),
                              e.rateFeeLineItemId,
                              RateFeeReportedField.Gross,
                              c.rateFeeLineItemId,
                            );
                          }}
                          onRemittanceBasisChangeProduct={
                            displayRemittanceBasis
                              ? (el: ChangeEvent<HTMLInputElement>) => {
                                  onChangeProduct?.(
                                    Number(el.target.value),
                                    e.rateFeeLineItemId,
                                    RateFeeReportedField.RemittanceBasis,
                                    c.rateFeeLineItemId,
                                  );
                                }
                              : undefined
                          }
                          onNetChangeProduct={(el: ChangeEvent<HTMLInputElement>) => {
                            onChangeProduct?.(
                              Number(el.target.value),
                              e.rateFeeLineItemId,
                              RateFeeReportedField.Net,
                              c.rateFeeLineItemId,
                            );
                          }}
                          onGrossClearProduct={() =>
                            onClear?.(e.rateFeeLineItemId, RateFeeReportedField.Gross, c.rateFeeLineItemId)
                          }
                          onRemittanceBasisClearProduct={
                            displayRemittanceBasis
                              ? () =>
                                  onClear?.(
                                    e.rateFeeLineItemId,
                                    RateFeeReportedField.RemittanceBasis,
                                    c.rateFeeLineItemId,
                                  )
                              : undefined
                          }
                          onNetClearProduct={() =>
                            onClear?.(e.rateFeeLineItemId, RateFeeReportedField.Net, c.rateFeeLineItemId)
                          }
                        />
                      </Grid>
                      {c.additionalFees &&
                        c.additionalFees.map((z: RateFee, k: number) => (
                          <Grid container key={z.rateFeeLineItemId} className={classes.tableRow}>
                            <ProductDescription
                              data={z}
                              isChildrenOfChild
                              displayNet={displayNet}
                              displayRemittanceBasis={displayRemittanceBasis}
                            />
                            <InputFields
                              data={z}
                              qaPrefix={qaPrefix}
                              qaSuffix={`${i + 1}${j + 1}${k + 1}`}
                              displayNet={displayNet}
                              displayRemittanceBasis={displayRemittanceBasis}
                              disableFields={disableFields}
                              showValidation={showValidation}
                              hasRemittedProduct={hasRemittedProduct}
                              onGrossChangeProduct={(el: ChangeEvent<HTMLInputElement>) => {
                                onChangeProduct?.(
                                  Number(el.target.value),
                                  e.rateFeeLineItemId,
                                  RateFeeReportedField.Gross,
                                  c.rateFeeLineItemId,
                                  z.rateFeeLineItemId,
                                );
                              }}
                              onRemittanceBasisChangeProduct={
                                displayRemittanceBasis
                                  ? (el: ChangeEvent<HTMLInputElement>) => {
                                      onChangeProduct?.(
                                        Number(el.target.value),
                                        e.rateFeeLineItemId,
                                        RateFeeReportedField.RemittanceBasis,
                                        c.rateFeeLineItemId,
                                        z.rateFeeLineItemId,
                                      );
                                    }
                                  : undefined
                              }
                              onNetChangeProduct={(el: ChangeEvent<HTMLInputElement>) => {
                                onChangeProduct?.(
                                  Number(el.target.value),
                                  e.rateFeeLineItemId,
                                  RateFeeReportedField.Net,
                                  c.rateFeeLineItemId,
                                  z.rateFeeLineItemId,
                                );
                              }}
                              onGrossClearProduct={() =>
                                onClear?.(
                                  e.rateFeeLineItemId,
                                  RateFeeReportedField.Gross,
                                  c.rateFeeLineItemId,
                                  z.rateFeeLineItemId,
                                )
                              }
                              onRemittanceBasisClearProduct={
                                displayRemittanceBasis
                                  ? () =>
                                      onClear?.(
                                        e.rateFeeLineItemId,
                                        RateFeeReportedField.RemittanceBasis,
                                        c.rateFeeLineItemId,
                                        z.rateFeeLineItemId,
                                      )
                                  : undefined
                              }
                              onNetClearProduct={() =>
                                onClear?.(
                                  e.rateFeeLineItemId,
                                  RateFeeReportedField.Net,
                                  c.rateFeeLineItemId,
                                  z.rateFeeLineItemId,
                                )
                              }
                            />
                          </Grid>
                        ))}
                    </>
                  ))}
              </>
            ))}
            <Grid container className={classes.totalsRow}>
              <Grid item sm={productSm} className={classes.totalsCell}>
                <Typography variant="body2" className={classes.weight500}>
                  Grand Total:
                </Typography>
              </Grid>
              <Grid item sm={3} className={clsx(classes.totalsCell, classes.tableCellRight)}>
                <Typography variant="body2" data-testid={`${qaPrefix}GrossTotal`} className={classes.weight500}>
                  {dollarize(grandTotal.gross)}
                </Typography>
              </Grid>
              {displayRemittanceBasis && (
                <Grid item sm={3} className={clsx(classes.totalsCell, classes.tableCellRight)}>
                  <Typography
                    variant="body2"
                    data-testid={`${qaPrefix}RemittanceBasisTotal`}
                    className={classes.weight500}
                  >
                    {dollarize(grandTotal.remittanceBasis)}
                  </Typography>
                </Grid>
              )}
              {displayNet && (
                <Grid item sm={3} className={clsx(classes.totalsCell, classes.tableCellRight)}>
                  <Typography variant="body2" data-testid={`${qaPrefix}NetTotal`} className={classes.weight500}>
                    {dollarize(grandTotal.net)}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Grid>
        </div>
      )}
    </>
  );
};

export default ReportedFeesTable;
